import { MapPin, Instagram, Globe, CalendarCheck, ChevronDown } from "lucide-react";
import { useState } from "react";

// ── Subtle, editorial "Holideviser Verified" indicator ───────────────────────
export const HolideviserVerified = ({ className = "" }) => (
  <span
    data-testid="holideviser-verified"
    className={`inline-flex items-center gap-2 font-sans text-[0.6rem] uppercase tracking-[0.24em] text-teal ${className}`}
    title="Personally experienced and approved by Holideviser"
  >
    <span className="inline-block h-1.5 w-1.5 rounded-full bg-teal" />
    Holideviser Verified
  </span>
);

// ── Signature "Holideviser Take" block ────────────────────────────────────────
export const HolideviserTake = ({ hook, take, testid = "holideviser-take" }) => {
  if (!hook && !take) return null;
  return (
    <div data-testid={testid} className="relative border-l-2 border-teal pl-6">
      <p className="font-sans text-[0.6rem] uppercase tracking-[0.28em] text-teal">Holideviser Take</p>
      {hook && (
        <p className="mt-3 font-serif text-2xl font-light italic leading-snug text-cream sm:text-3xl">
          “{hook}”
        </p>
      )}
      {take && <p className="mt-4 max-w-xl font-sans text-base leading-relaxed text-sand">{take}</p>}
    </div>
  );
};

// ── "Know Before You Go" block — noticeable, not a warning box ────────────────
export const KnowBeforeYouGo = ({ text, testid = "know-before-you-go" }) => {
  if (!text) return null;
  return (
    <div data-testid={testid} className="border border-teal/30 bg-teal/[0.05] p-5 sm:p-6">
      <p className="font-sans text-[0.6rem] uppercase tracking-[0.26em] text-teal">Know Before You Go</p>
      <p className="mt-3 font-sans text-sm leading-relaxed text-cream/90">{text}</p>
    </div>
  );
};

// ── "Holideviser Tip" block — a warm, editorial pointer from firsthand experience
export const HolideviserTip = ({ text, testid = "holideviser-tip" }) => {
  if (!text) return null;
  return (
    <div data-testid={testid} className="border-l-2 border-teal/50 pl-5 sm:pl-6">
      <p className="font-sans text-[0.6rem] uppercase tracking-[0.26em] text-teal">Holideviser Tip</p>
      <p className="mt-3 font-sans text-sm leading-relaxed text-cream/90">{text}</p>
    </div>
  );
};

const Chips = ({ label, items, testid }) => {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-sand">{label}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {items.map((it) => (
          <span
            key={it}
            className="border border-charcoal px-3 py-1 font-sans text-xs text-cream/85"
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
};

const Detail = ({ label, value, accent }) => {
  if (!value) return null;
  return (
    <div>
      <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-sand">{label}</p>
      <p className={`mt-2 font-sans text-sm leading-relaxed ${accent ? "text-teal" : "text-cream/90"}`}>
        {value}
      </p>
    </div>
  );
};

const LinkPill = ({ href, icon: Icon, children, testid }) =>
  href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="inline-flex items-center gap-2 border border-charcoal px-4 py-2 font-sans text-[0.65rem] uppercase tracking-[0.16em] text-sand transition-colors hover:border-teal hover:text-teal"
    >
      <Icon size={14} strokeWidth={1.6} />
      {children}
    </a>
  ) : null;

// ── Full editorial recommendation card ────────────────────────────────────────
export const RecommendationCard = ({ rec, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const verified = rec.verification_status === "PERSONALLY_VERIFIED";
  const place = [rec.neighborhood, rec.destination, rec.country].filter(Boolean).join(", ");
  const hasLinks = rec.maps_url || rec.website_url || rec.instagram_url || rec.booking_url;

  return (
    <article
      data-testid={`recommendation-${rec.id}`}
      className="overflow-hidden border border-charcoal bg-charcoal/20"
    >
      {/* Hero image */}
      {rec.hero_image && (
        <div className="relative aspect-[16/9] w-full overflow-hidden sm:aspect-[21/9]">
          <img
            src={rec.hero_image}
            alt={rec.alt_text || rec.name}
            loading="lazy"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
          {["PLACEHOLDER", "ILLUSTRATIVE"].includes(rec.photo_source) && (
            <span
              data-testid={`illustrative-caption-${rec.id}`}
              className="absolute right-3 top-3 rounded-none bg-ink/70 px-2.5 py-1 font-sans text-[0.58rem] uppercase tracking-[0.12em] text-cream/80 backdrop-blur-sm"
            >
              Illustrative image — not a photograph of this venue
            </span>
          )}
          <div className="absolute bottom-5 left-5 right-5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="font-sans text-[0.6rem] uppercase tracking-[0.26em] text-cream/70">
                {rec.subcategory || rec.category}
              </span>
              {verified && <HolideviserVerified />}
            </div>
            <h3 className="mt-2 font-serif text-3xl font-light leading-tight text-cream sm:text-4xl">
              {rec.name}
            </h3>
            {place && (
              <p className="mt-1 font-sans text-xs uppercase tracking-[0.14em] text-sand">{place}</p>
            )}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="p-6 sm:p-8">
        {!rec.hero_image && (
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="font-sans text-[0.6rem] uppercase tracking-[0.26em] text-sand">
                {rec.subcategory || rec.category}
              </span>
              {verified && <HolideviserVerified />}
            </div>
            <h3 className="mt-2 font-serif text-3xl font-light text-cream sm:text-4xl">{rec.name}</h3>
            {place && <p className="mt-1 font-sans text-xs uppercase tracking-[0.14em] text-sand">{place}</p>}
          </div>
        )}

        <HolideviserTake hook={rec.short_hook} take={rec.holideviser_take} testid={`take-${rec.id}`} />

        {/* Holideviser Tip — surfaced high, from firsthand experience */}
        {rec.holideviser_tip && (
          <div className="mt-8">
            <HolideviserTip text={rec.holideviser_tip} testid={`tip-${rec.id}`} />
          </div>
        )}

        {/* Know before you go — surfaced high on the card */}
        {rec.know_before_you_go && (
          <div className="mt-8">
            <KnowBeforeYouGo text={rec.know_before_you_go} testid={`kbyg-${rec.id}`} />
          </div>
        )}

        {/* Expandable detail */}
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid={`toggle-details-${rec.id}`}
          className="mt-8 inline-flex items-center gap-2 border-b border-teal/60 pb-1 font-sans text-xs uppercase tracking-[0.18em] text-cream transition-colors hover:text-teal"
        >
          {open ? "Less" : "The full note"}
          <ChevronDown
            size={15}
            strokeWidth={1.6}
            className={`text-teal transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div className="mt-8 flex flex-col gap-8" data-testid={`details-${rec.id}`}>
            {rec.editorial_description && (
              <p className="max-w-2xl font-sans text-sm leading-relaxed text-cream/85">
                {rec.editorial_description}
              </p>
            )}

            {rec.drink_concept && (
              <div>
                <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-sand">
                  The drink concept{rec.seasonal_or_rotating_menu ? " · Rotating" : ""}
                </p>
                <p className="mt-2 max-w-2xl font-sans text-sm leading-relaxed text-cream/85">
                  {rec.drink_concept}
                </p>
              </div>
            )}

            {rec.food_note && (
              <div>
                <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-sand">Food</p>
                <p className="mt-2 max-w-2xl font-sans text-sm leading-relaxed text-cream/85">
                  {rec.food_note}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Chips label="Best for" items={rec.best_for} testid={`bestfor-${rec.id}`} />
              <Chips label="Vibe" items={rec.vibe} testid={`vibe-${rec.id}`} />
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Detail label="Go for" value={rec.go_for} accent />
              <Detail label="Don't go for" value={rec.dont_go_for} />
              <Detail label="Don't expect" value={rec.dont_expect} />
              <Detail label="Try / What to order" value={rec.try_or_order} />
              <Detail label="Best moment" value={rec.best_moment} />
              <Detail label="Best time" value={rec.best_time} />
            </div>

            {rec.founder_experience && (
              <div className="border-t border-charcoal pt-6">
                <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-sand">
                  Founder experience
                </p>
                <p className="mt-2 max-w-2xl font-sans text-sm italic leading-relaxed text-cream/80">
                  {rec.founder_experience}
                </p>
              </div>
            )}

            {hasLinks && (
              <div className="flex flex-wrap gap-3">
                <LinkPill href={rec.maps_url} icon={MapPin} testid={`map-${rec.id}`}>Map</LinkPill>
                <LinkPill href={rec.website_url} icon={Globe} testid={`web-${rec.id}`}>Website</LinkPill>
                <LinkPill href={rec.instagram_url} icon={Instagram} testid={`ig-${rec.id}`}>Instagram</LinkPill>
                <LinkPill href={rec.booking_url} icon={CalendarCheck} testid={`book-${rec.id}`}>Book</LinkPill>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
};
